import { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import {
  AlertSeverity,
  JobDetail,
  setAlert,
  SubmitReviewErrorBody,
  useAppDispatch,
  useAppSelector,
  useSubmitReviewMutation,
  ValidationError,
} from 'api';
import { FieldDecisionState, toSubmitDecision } from '../decisionState';

type SubmitBarProps = {
  jobDetail: JobDetail;
  decisions: Record<string, FieldDecisionState>;
  /** Called with each attempt's errors (empty on success), so the panels
   * holding them can be expanded. */
  onValidationErrors?: (errors: ValidationError[]) => void;
};

/** Submits the decided fields; at least one is required. Undecided fields
 * aren't sent and can be decided in a later submission. All validation errors
 * come back together and are listed. */
export const SubmitBar = ({
  jobDetail,
  decisions,
  onValidationErrors,
}: SubmitBarProps) => {
  const dispatch = useAppDispatch();
  const { mockUser } = useAppSelector(state => state.appUser);
  const uid = mockUser?.id.toString() ?? '';
  const [submitReview, { isLoading }] = useSubmitReviewMutation();
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const decidedFields = jobDetail.fields.filter(
    f => decisions[f.fieldName]?.isCorrect != null
  );
  const canSubmit = decidedFields.length > 0;

  const handleSubmit = async () => {
    setErrors([]);
    onValidationErrors?.([]);
    const request = {
      decisions: decidedFields.map(f =>
        toSubmitDecision(f.fieldName, decisions[f.fieldName])
      ),
    };

    try {
      await submitReview({
        jobKey: jobDetail.extractionJobKey,
        uid,
        request,
      }).unwrap();
      dispatch(
        setAlert({
          message: 'Review submitted.',
          severity: AlertSeverity.Success,
        })
      );
    } catch (err: any) {
      const body = err?.data as SubmitReviewErrorBody | undefined;
      const next = body?.errors?.length
        ? body.errors
        : [{ field: null, message: 'Submit failed. Try again.' }];
      setErrors(next);
      onValidationErrors?.(next);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {errors.map((e, i) => (
        <Alert severity="error" key={i} sx={{ mb: 1 }}>
          {e.field ? `${e.field}: ${e.message}` : e.message}
        </Alert>
      ))}
      <Button
        variant="contained"
        fullWidth
        disabled={!canSubmit || isLoading}
        loading={isLoading}
        loadingPosition="start"
        onClick={handleSubmit}
      >
        Submit review
      </Button>
    </Box>
  );
};
